import { useMemo } from 'react';
import { calculateDistance } from '@/lib/distance';
import { checkOrderAvailability, type Restaurant, type OrderType } from '@/lib/operatingHours';

interface RestaurantWithAvailability extends Restaurant {
  isOrderAvailable?: boolean;
  distance?: number;
  hasActiveOffer?: boolean;
  // CRITICAL: Explicitly include operating hours for preservation
  operating_hours?: any[];
  todayOperatingHours?: any;
}

interface FilterOptions {
  selectedStoreTypes: string[];
  showOpenOnly: boolean;
  showClosedOnly: boolean;
}

interface LocationCoordinate {
  latitude: number;
  longitude: number;
}

type SortOrder = 'newest' | 'distance';

export function useStoreFiltering(
  restaurants: Restaurant[],
  selectedDiscountType: OrderType,
  sortOrder: SortOrder,
  filterOptions: FilterOptions,
  currentLocation?: LocationCoordinate | null
) {
  return useMemo(() => {
    // Step 1: 초기 데이터 처리 (주문 가능 여부, 거리 계산)
    let processed: RestaurantWithAvailability[] = restaurants.map(restaurant => {
      const orderAvailable = checkOrderAvailability(restaurant, selectedDiscountType);
      const distance = currentLocation && restaurant.latitude && restaurant.longitude
        ? calculateDistance(
            currentLocation.latitude,
            currentLocation.longitude,
            restaurant.latitude,
            restaurant.longitude
          )
        : undefined;
      
      // 마감할인의 경우 오퍼 여부도 체크
      let finalOrderAvailable = orderAvailable;
      if (selectedDiscountType === 'DEADLINE_DISCOUNT') {
        const hasOffer = (restaurant as any).has_active_offer_today === true;
        // 오퍼가 없으면 주문 불가능
        finalOrderAvailable = hasOffer && orderAvailable;
      }
      
      return { 
        ...restaurant, 
        isOrderAvailable: finalOrderAvailable, 
        distance,
        // 마감할인 시 오퍼 여부 표시 (UI에서 활용 가능)
        hasActiveOffer: selectedDiscountType === 'DEADLINE_DISCOUNT' 
          ? (restaurant as any).has_active_offer_today === true 
          : undefined,
        // CRITICAL: Ensure operating_hours data is explicitly preserved
        operating_hours: restaurant.operating_hours,
        todayOperatingHours: (restaurant as any).todayOperatingHours
      } as RestaurantWithAvailability;
    });

    // Step 2: 매장 타입 필터
    if (filterOptions.selectedStoreTypes.length > 0) {
      processed = processed.filter(restaurant => 
        filterOptions.selectedStoreTypes.includes(restaurant.store_type || '')
      );
    }

    // Step 3: 영업 상태 필터 (isOrderAvailable 기반으로 변경)
    if (filterOptions.showOpenOnly) {
      // "영업 중" = 실제로는 "주문 가능"
      processed = processed.filter(restaurant => restaurant.isOrderAvailable);
    } else if (filterOptions.showClosedOnly) {
      // "영업종료" = 실제로는 "주문 불가"  
      processed = processed.filter(restaurant => !restaurant.isOrderAvailable);
    }

    // Step 4: 정렬
    let sorted = processed;
    
    // 영업 필터 미적용 시 주문 가능/불가능 그룹 분리
    if (!filterOptions.showOpenOnly && !filterOptions.showClosedOnly) {
      const available = processed.filter(r => r.isOrderAvailable);
      const unavailable = processed.filter(r => !r.isOrderAvailable);
      
      // 각 그룹 내에서 정렬
      if (sortOrder === 'newest') {
        available.sort((a, b) => 
          new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime()
        );
        unavailable.sort((a, b) => 
          new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime()
        );
      } else if (sortOrder === 'distance') {
        if (currentLocation && 
            currentLocation.latitude !== 0 && 
            currentLocation.longitude !== 0) {
          available.sort((a, b) => (a.distance ?? Infinity) - (b.distance ?? Infinity));
          unavailable.sort((a, b) => (a.distance ?? Infinity) - (b.distance ?? Infinity));
        } else {
        }
      }
      
      // 주문 가능한 것 먼저, 불가능한 것 나중에
      sorted = [...available, ...unavailable];
    } else {
      // 영업 필터 적용 시 전체 정렬
      if (sortOrder === 'newest') {
        sorted.sort((a, b) => 
          new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime()
        );
      } else if (sortOrder === 'distance') {
        if (currentLocation && 
            currentLocation.latitude !== 0 && 
            currentLocation.longitude !== 0) {
          sorted.sort((a, b) => (a.distance ?? Infinity) - (b.distance ?? Infinity));
        } else {
        }
      }
    }

    return sorted;
  }, [restaurants, selectedDiscountType, sortOrder, filterOptions, currentLocation]);
}